import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Caption1 } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import UserLink from "~/components/UserLink/UserLink";
import { useVersionContext } from "./VersionContext";
import { VersionOptions } from "./VersionOptions";
import { FormatSmartLocalDateTime } from "../Formatters/FormatSmartLocalDateTime";

export const VersionItem = ({
  id,
  versionName,
  modificationId,
  createdById,
  createdByName,
  createdByEmail,
  createdOn,
  className,
  onClick,
  onRename,
  onRevert,
}) => {
  const { t } = useTranslation();
  const { versionId, setVersionId, setModificationId } = useVersionContext();

  const active = id === versionId;

  if (modificationId === 0) {
    versionName = t("Original");
  }

  const handleClick = useCallback(() => {
    setVersionId(id);
    setModificationId(modificationId);
    typeof onClick === "function" && onClick(modificationId);
  }, [setVersionId, id, setModificationId, modificationId, onClick]);

  return (
    <div
      className={classNames(
        "tw-flex tw-flex-col tw-border tw-border-solid tw-border-theme-400 ac-border-radius-8 tw-p-2 tw-cursor-pointer tw-relative note-version-item",
        { "tw-bg-primary-200": active },
        className
      )}
      onClick={handleClick}
    >
      <VersionOptions
        name={versionName ?? ""}
        modificationId={modificationId}
        className="tw-absolute tw-right-2"
        onRename={onRename}
        onRevert={onRevert}
        versionId={id}
      />
      <div className="tw-flex tw-items-center tw-h-5 tw-mb-1">
        <Body2
          weight="medium"
          color="tertiary"
        >
          #{id}
        </Body2>
        <Body2
          weight="medium"
          color={versionName ? "secondary" : "tertiary"}
          className="tw-truncate"
        >
          <span className="tw-mx-1">•</span>
          {versionName ?? t("Version")}
        </Body2>
      </div>

      <Caption1
        weight="regular"
        color="tertiary"
        className="tw-flex tw-items-center"
      >
        <FormatSmartLocalDateTime value={createdOn} />
        <span className="tw-mx-1">•</span>
        <UserLink
          id={createdById}
          name={createdByName}
          email={createdByEmail}
          hideAvatar
          notInteractive
          nameProps={{
            weight: "medium",
            color: "tertiary",
            variant: "Caption1",
          }}
          className="tw-truncate tw-flex-1"
          context="note version item"
        />
      </Caption1>
    </div>
  );
};

VersionItem.propTypes = {
  id: PropTypes.number.isRequired,
  versionName: PropTypes.string,
  modificationId: PropTypes.number,
  createdByName: PropTypes.string,
  createdByEmail: PropTypes.string.isRequired,
  createdOn: PropTypes.number.isRequired,
  createdById: PropTypes.number,
  className: PropTypes.string,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  onRename: PropTypes.func,
  onRevert: PropTypes.func,
};
